import * as React from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { MemberPortalAutopay } from '@/components/member-portal/MemberPortalAutopay'
import { LevelChangeSection } from '@/components/member-portal/MemberPortalLevelChange'
import { deleteJSON, getJSON, postJSON } from '@/lib/api'
import {
  displayPortalDate,
  canDeclareCancellation,
  membershipBadgeVariant,
  type MemberStatusSource,
  type MemberPortalMembership as Membership,
} from '@/components/member-portal/memberPortalPresentation'
import { MemberStatusSources } from '@/components/member-portal/MemberStatusSources'
import { MemberPortalConferredAccess } from '@/components/member-portal/MemberPortalOrganizationAccess'
export function MemberPortalMembership({
  associationName,
  membership,
  memberAccess,
  memberStatusSources,
}: {
  associationName: string
  membership?: Membership | null
  memberAccess?: boolean
  memberStatusSources?: MemberStatusSource[]
}) {
  const [scheduledEndOn, setScheduledEndOn] = React.useState<string | null>(null)
  const [showCancellationConfirmation, setShowCancellationConfirmation] = React.useState(false)
  const queryClient = useQueryClient()
  const undoKey = React.useRef(`portal-cancellation-undo-${crypto.randomUUID()}`)
  React.useEffect(() => {
    setScheduledEndOn(
      membership?.status === 'Lapsed' ? null : (membership?.scheduled_cancellation_end_on ?? null),
    )
  }, [membership?.scheduled_cancellation_end_on, membership?.status])
  const undo = useMutation({
    mutationFn: () =>
      deleteJSON<CancellationResponse>('/api/v1/member_portal/cancellation', {
        cancellation: { idempotency_key: undoKey.current },
      }),
    onSuccess: () => {
      setScheduledEndOn(null)
      queryClient.invalidateQueries({ queryKey: ['member-portal-session'] })
    },
  })
  if (!membership) {
    if (memberAccess !== true) return <NonMemberContent associationName={associationName} />
    return <MemberPortalConferredAccess sources={memberStatusSources ?? []} />
  }
  if (membership.status === 'Pending-New') {
    return <PendingNewApplication membership={membership} />
  }
  if (showCancellationConfirmation) {
    return (
      <CancellationConfirmation
        renewalDueOn={membership.renewal_due_on}
        onBack={() => setShowCancellationConfirmation(false)}
        onScheduled={(endOn) => {
          setScheduledEndOn(endOn)
          setShowCancellationConfirmation(false)
        }}
      />
    )
  }
  return (
    <>
      <Card className="mt-6 overflow-hidden">
        <div className="flex items-center justify-between gap-5 p-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-body font-medium">{membership.level}</p>
              <Badge
                variant={membershipBadgeVariant(scheduledEndOn, membership.status)}
                className="whitespace-nowrap"
              >
                {scheduledEndOn
                  ? `Cancels on ${displayPortalDate(scheduledEndOn)}`
                  : membership.status}
              </Badge>
            </div>
            <p className="mt-1 text-caption text-muted-foreground">
              {membershipCaption(membership)}
            </p>
          </div>
          <div className="shrink-0 text-right">
            {membership.status === 'Lapsed' ? (
              <LapsedRejoinAction membership={membership} />
            ) : (
              <>
                <p className="text-body-sm font-medium tabular-nums">
                  {membership.yearly_price_label ?? membership.yearly_price}/year
                </p>
                {membership.renewal_due_on && (
                  <p className="mt-1 text-caption text-muted-foreground">
                    Renews {displayPortalDate(membership.renewal_due_on)}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
        {membership.status === 'Suspended' ? (
          <div className="border-t border-border px-4 py-3">
            <p className="text-caption text-muted-foreground">
              Rejoining is unavailable for this Membership.
            </p>
          </div>
        ) : null}
        <MembershipFooter
          membership={membership}
          scheduledEndOn={scheduledEndOn}
          onCancellation={() => setShowCancellationConfirmation(true)}
          onUndo={() => undo.mutate()}
          undoPending={undo.isPending}
        />
      </Card>
      <MemberPortalExtras membership={membership} />
      <MemberStatusSources sources={memberStatusSources ?? []} />
    </>
  )
}
function MemberPortalExtras({ membership }: { membership: Membership }) {
  if (membership.status === 'Lapsed' || membership.status === 'Suspended') return null
  return (
    <>
      <MemberPortalAutopay />
      <LevelChangeSection membership={membership} />
    </>
  )
}
function NonMemberContent({ associationName }: { associationName: string }) {
  return (
    <section className="mt-6 flex items-center justify-between gap-4">
      <p className="text-body-sm text-muted-foreground">Not a member</p>
      <Button asChild variant="outline" size="sm">
        <a href="/join">Join {associationName}</a>
      </Button>
    </section>
  )
}
type RejoinAvailability = { rejoin: { available: boolean } }
type RejoinInvoice = { invoice: { id: number; total: string } }
function LapsedRejoinAction({ membership }: { membership: Membership }) {
  const idempotencyKey = React.useRef(`portal-rejoin-${crypto.randomUUID()}`)
  const availability = useQuery({
    queryKey: ['member-portal-rejoin'],
    queryFn: () => getJSON<RejoinAvailability>('/api/v1/member_portal/rejoin'),
  })
  const rejoin = useMutation({
    mutationFn: () =>
      postJSON<RejoinInvoice>('/api/v1/member_portal/rejoin', {
        rejoin: { idempotency_key: idempotencyKey.current },
      }),
  })
  if (availability.isLoading) {
    return <span className="text-caption text-muted-foreground">Checking Rejoin...</span>
  }
  if (availability.isError || !availability.data?.rejoin.available) {
    return (
      <span className="text-caption text-muted-foreground">
        Rejoin is unavailable. Contact your association.
      </span>
    )
  }
  if (rejoin.data) {
    return (
      <div className="flex flex-col items-end gap-1">
        <Link
          to={`/portal/billing/invoices/${rejoin.data.invoice.id}`}
          className="text-body-sm text-primary underline-offset-2 hover:underline"
        >
          Open rejoin Invoice · {rejoin.data.invoice.total}
        </Link>
        <span className="text-caption text-muted-foreground">
          Rejoining starts a fresh year today.
        </span>
      </div>
    )
  }
  return (
    <div className="flex flex-col items-end gap-1">
      <Button type="button" size="sm" onClick={() => rejoin.mutate()} disabled={rejoin.isPending}>
        {rejoin.isPending
          ? 'Preparing Rejoin...'
          : `Rejoin — ${membership.yearly_price_label ?? membership.yearly_price}/year`}
      </Button>
      <span className="text-caption text-muted-foreground">
        Rejoining starts a fresh year today.
      </span>
    </div>
  )
}
type CancellationResponse = { cancellation: { scheduled_end_on: string; reason: string | null } }
function MembershipFooter({
  membership,
  scheduledEndOn,
  onCancellation,
  onUndo,
  undoPending,
}: {
  membership: Membership
  scheduledEndOn: string | null
  onCancellation: () => void
  onUndo: () => void
  undoPending: boolean
}) {
  if (scheduledEndOn) {
    return (
      <div className="flex items-center justify-between gap-4 border-t border-border px-4 py-3">
        <p className="text-body-sm">Cancels on {displayPortalDate(scheduledEndOn)}.</p>
        <Button type="button" variant="outline" size="sm" onClick={onUndo} disabled={undoPending}>
          {undoPending ? 'Undoing...' : 'Undo'}
        </Button>
      </div>
    )
  }
  if (!membership.member_self_cancellation_enabled) {
    return (
      <div className="border-t border-border px-4 py-3">
        {membership.renewal_state && <RenewalState state={membership.renewal_state} />}
        <p
          className={`text-caption text-muted-foreground ${membership.renewal_state ? 'mt-3' : ''}`}
        >
          Membership departures are handled by the association.
        </p>
      </div>
    )
  }
  if (!canDeclareCancellation(membership))
    return membership.renewal_state ? (
      <div className="border-t border-border px-4 py-3">
        <RenewalState state={membership.renewal_state} />
      </div>
    ) : null
  return (
    <div className="border-t border-border px-4 py-3">
      {membership.renewal_state && <RenewalState state={membership.renewal_state} />}
      <div className={membership.renewal_state ? 'mt-3' : ''}>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="px-0 text-destructive shadow-none hover:bg-transparent hover:text-destructive"
          onClick={onCancellation}
        >
          Cancel membership
        </Button>
      </div>
    </div>
  )
}
function CancellationConfirmation({
  renewalDueOn,
  onBack,
  onScheduled,
}: {
  renewalDueOn: string | null
  onBack: () => void
  onScheduled: (endOn: string) => void
}) {
  const queryClient = useQueryClient()
  const [reason, setReason] = React.useState('')
  const declaration = useMutation({
    mutationFn: () =>
      postJSON<CancellationResponse>('/api/v1/member_portal/cancellation', {
        cancellation: reason ? { reason } : {},
      }),
    onSuccess: ({ cancellation }) => {
      queryClient.invalidateQueries({ queryKey: ['member-portal-session'] })
      onScheduled(cancellation.scheduled_end_on)
    },
  })
  const endDate = renewalDueOn ? displayPortalDate(renewalDueOn) : 'your renewal-due date'
  return (
    <div className="mt-6 max-w-xl space-y-4">
      <Button
        type="button"
        variant="link"
        size="inline"
        className="text-muted-foreground hover:text-foreground"
        onClick={onBack}
      >
        ← Membership
      </Button>
      <Card>
        <div className="space-y-3 p-4">
          <h1 className="text-body font-semibold">Cancel membership</h1>
          <p className="text-body-sm text-muted-foreground">
            Your membership stays active through {endDate}. After that it ends and no renewal
            invoice is sent.
          </p>
          <div className="space-y-2 pt-1">
            <Label htmlFor="cancellation-reason">Reason (optional)</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger id="cancellation-reason">
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Too expensive">Too expensive</SelectItem>
                <SelectItem value="Not using it">Not using it</SelectItem>
                <SelectItem value="Moving away">Moving away</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {declaration.isError && <Alert>Cancellation could not be scheduled.</Alert>}
        </div>
        <div className="flex flex-wrap items-center gap-2 border-t border-border p-4">
          <Button
            type="button"
            variant="quietDestructive"
            size="sm"
            onClick={() => declaration.mutate()}
            disabled={declaration.isPending}
          >
            {declaration.isPending ? 'Cancelling...' : 'Cancel membership'}
          </Button>
          <Button type="button" variant="ghost" size="sm" className="shadow-none" onClick={onBack}>
            Keep membership
          </Button>
        </div>
      </Card>
    </div>
  )
}
function PendingNewApplication({ membership }: { membership: Membership }) {
  return (
    <Card className="mt-6 overflow-hidden">
      <div className="flex items-center justify-between gap-5 p-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-body font-medium">{membership.level}</p>
            <Badge variant="neutral" className="whitespace-nowrap">
              Awaiting review
            </Badge>
          </div>
          <p className="mt-1 text-caption text-muted-foreground">
            Applied {displayPortalDate(membership.application_submitted_on ?? '')}
          </p>
        </div>
      </div>
      <p className="border-t border-border px-4 py-3 text-caption text-muted-foreground">
        Your application is awaiting review.
      </p>
    </Card>
  )
}
function RenewalState({ state }: { state: NonNullable<Membership['renewal_state']> }) {
  if (state.kind === 'scheduled') {
    return (
      <p className="text-caption text-muted-foreground">
        Your renewal invoice will arrive around {displayPortalDate(state.arrival_on)}.
      </p>
    )
  }
  return (
    <Link
      to={state.invoice_path}
      className="text-body-sm text-primary underline-offset-2 hover:underline"
    >
      Renewal invoice {state.invoice_number} open · {state.amount} — Pay
    </Link>
  )
}
function membershipCaption(membership: Membership) {
  const number = membership.member_number ? `Member #${membership.member_number}` : null
  const since = membership.member_since_on
    ? `since ${displayPortalDate(membership.member_since_on)}`
    : null
  return [number, since].filter(Boolean).join(' · ')
}
